import React from 'react';
import api from 'controllers/raymanAPI';
import {FilesCtxProvider, FilesCtx} from './files';
import Singletons from 'controllers/Singletons';

function map(item)
{
    return {...item, id: item.verificationID, fileDownloadURL: api.procedures.tasks.reports.verifications.file.fileDownloadURL(item.taskID, item.stepIndex, item.verificationID)}
}

async function getData(taskID, stepIndex)
{
    let indexed = {};

    let response = await api.procedures.tasks.reports.verifications.getAll(taskID, stepIndex);
    let data = await response.json();
    data = data.map((item)=>map(item));
    data.forEach(item => {
        indexed[item.verificationID] = item
    });
    
    return {array: data, indexed: indexed};
}

const Context = React.createContext({
    loading: true, 
    data: {
        array: [], 
        indexed: {}
    }, 
    funcs: {
        create: () => {},
        update: () => {},
        delete: () => {},
    }
});

function CtxProvider({taskID, stepIndex, ...props})
{

    const [loading, setLoading] = React.useState(true);
    const [data, setData] = React.useState({array: [], indexed: []});

    React.useEffect(() => {
        if(!taskID || stepIndex === undefined || stepIndex === null) return;
        getData(taskID, stepIndex).then(values => {
            setData(values);
            setLoading(false);
        });
    }, [taskID, stepIndex]);

    const internalRefresh = React.useCallback(() => {
        getData(taskID, stepIndex).then(values => {
            setData(values);
            setLoading(false);
        });
    }, [taskID, stepIndex]);

    const internalUpdate = React.useCallback((verificationID, body) => {
        let tmp = {...data};
        if(body.order && body.order !== tmp.indexed[verificationID].order) internalRefresh();
        else {
            Object.keys(body).forEach((key) => {
                tmp.indexed[verificationID][key] = body[key];
            })
            setData(tmp);
        }
    }, [data, internalRefresh]);

    const internalAdd = React.useCallback(async (verificationID, item) => {
        if(!taskID || stepIndex === undefined || stepIndex === null) return;
        if(data.indexed[verificationID] !== undefined) return internalUpdate(verificationID, map(item));
        
        let tmp = {...data};
        item = map(item);
        let index = tmp.array.push(item);
        tmp.indexed[item.id] = tmp.array[index-1];
        setData(tmp);
    }, [internalUpdate, taskID, stepIndex, data]);

    
    const internalDelete = React.useCallback((verificationID) => {
        let tmp = {...data};
        let item = tmp.indexed[verificationID];

        // remove from array
        let index = tmp.array.indexOf(item);
        if(index > -1) tmp.array.splice(index,1);

        delete tmp.indexed[verificationID];
        setData(tmp);
    }, [data]);    


    const create = React.useCallback(async (data) => {
        if(!taskID || stepIndex === undefined || stepIndex === null) return;
        let result = await api.procedures.tasks.reports.verifications.post(taskID, stepIndex, data);
        if(result.ok)
        {
            let report = await result.json();
            await internalAdd(report.verificationID, report);
            return report;
        }
    }, [taskID, stepIndex, internalAdd]);

    const update = React.useCallback(async (verificationID, data) => {
        if(!taskID || stepIndex === undefined || stepIndex === null) return;
        let result = await api.procedures.tasks.reports.verifications.updateSingle(taskID, stepIndex, verificationID, data);
        if(result.ok) internalUpdate(verificationID, map(await result.json()));

    }, [taskID, stepIndex, internalUpdate]);

    const remove = React.useCallback(async (verificationID) => {
        if(!taskID || stepIndex === undefined || stepIndex === null) return;
        let result = await api.procedures.tasks.reports.verifications.deleteSingle(taskID, stepIndex, verificationID);
        if(result.ok) internalDelete(verificationID);
    }, [taskID, stepIndex, internalDelete]);

    React.useEffect( function handleNotifications() {

        const handleCreate = (message) => {if(message.stepIndex === stepIndex && message.taskID === taskID) internalAdd(message.verificationID, message) };
        const handleUpdate = (message) => {if(message.stepIndex === stepIndex && message.taskID === taskID) internalAdd(message.verificationID, message) };
        const handleDelete = (message) => {if(message.stepIndex === stepIndex && message.taskID === taskID) internalDelete(message.verificationID, message) };

        Singletons.NotificationsSocket.on('procedures.tasks.reports.verifications.created', handleCreate);
        Singletons.NotificationsSocket.on('procedures.tasks.reports.verifications.updated', handleUpdate);
        Singletons.NotificationsSocket.on('procedures.tasks.reports.verifications.deleted', handleDelete);

        return () => {
            Singletons.NotificationsSocket.off('procedures.tasks.reports.verifications.created', handleCreate);
            Singletons.NotificationsSocket.off('procedures.tasks.reports.verifications.updated', handleUpdate);
            Singletons.NotificationsSocket.off('procedures.tasks.reports.verifications.deleted', handleDelete);
        }

    }, [internalAdd, internalUpdate, internalDelete, stepIndex, taskID]);

    return <Context.Provider value={{loading: loading, data: {...data}, funcs: {create: create, update: update, delete: remove}}} >
        {props.children}
        </Context.Provider>
}


function FilesCtxProviderWrapper({taskID, stepIndex, verificationID, ...props})
{
    return <FilesCtxProvider apiEndpoint={api.procedures.tasks.reports.verifications.files} apiParams={[taskID, stepIndex, verificationID]}>
            <FilesNotificationHandler taskID={taskID} stepIndex={stepIndex} verificationID={verificationID}>
                {props.children}
            </FilesNotificationHandler>
        </FilesCtxProvider>
}


function FilesNotificationHandler({taskID, stepIndex, verificationID, ...props})
{
    const filesCtx = React.useContext(FilesCtx);

    React.useEffect( function handleChatNotifications() {

        const addFile = (message) => {if(message.taskID === taskID && message.stepIndex === stepIndex && message.verificationID === verificationID) filesCtx.funcs.internal.add(message.fileInfo.fileID, message.fileInfo);}
        const deleteFile = (message) => {if(message.taskID === taskID && message.stepIndex === stepIndex && message.verificationID === verificationID) filesCtx.funcs.internal.delete(message.fileID);}
        const refreshFile = (message) => {if(message.taskID === taskID && message.stepIndex === stepIndex && message.verificationID === verificationID) filesCtx.funcs.internal.refresh(message.fileInfo.fileID);}

        Singletons.NotificationsSocket.on('procedures.tasks.reports.verifications.files.created', addFile);
        Singletons.NotificationsSocket.on('procedures.tasks.reports.verifications.files.deleted', deleteFile);
        Singletons.NotificationsSocket.on('procedures.tasks.reports.verifications.files.updated', refreshFile);

        return () => {
            Singletons.NotificationsSocket.off('procedures.tasks.reports.verifications.files.created', addFile);
            Singletons.NotificationsSocket.off('procedures.tasks.reports.verifications.files.deleted', deleteFile);
            Singletons.NotificationsSocket.off('procedures.tasks.reports.verifications.files.updated', refreshFile);
        }
    }, [filesCtx, taskID, verificationID, stepIndex]);

    return <>{props.children}</>;

}

export {Context as VerificationsCtx, CtxProvider as VerificationsCtxProvider, FilesCtxProviderWrapper as VerificationsFilesCtxProvider};
export {FilesCtx as VerificationsFilesCtx} from './files';